/**
 * Smart Merge Utility
 * 
 * Intelligently merges AI-generated requirements with existing requirements
 * Uses similarity matching to identify added, modified, removed, and unchanged requirements
 */

import { Requirements } from '../../../../../Entities/JobDescription';

export interface MergeResult {
  mergedRequirements: Requirements[];
  changes: {
    added: Requirements[];
    modified: Requirements[];
    removed: Requirements[];
    unchanged: Requirements[];
  };
}

/**
 * Calculate similarity between two requirement descriptions
 * Uses simple string similarity (can be enhanced with fuzzy matching library)
 * 
 * @param desc1 - First description
 * @param desc2 - Second description
 * @returns Similarity score between 0 and 1
 */
const calculateSimilarity = (desc1: string, desc2: string): number => {
  const normalize = (str: string) => str.toLowerCase().trim().replace(/\s+/g, ' ');
  const norm1 = normalize(desc1);
  const norm2 = normalize(desc2);

  // Exact match
  if (norm1 === norm2) {
    return 1.0;
  }

  // Check if one contains the other (high similarity)
  if (norm1.includes(norm2) || norm2.includes(norm1)) {
    return 0.8;
  }

  // Calculate word overlap
  const words1 = new Set(norm1.split(' '));
  const words2 = new Set(norm2.split(' '));
  const words1Array = Array.from(words1);
  const words2Array = Array.from(words2);
  const intersection = new Set(words1Array.filter((x) => words2.has(x)));
  const union = new Set([...words1Array, ...words2Array]);
  
  if (union.size === 0) {
    return 0;
  }

  // Jaccard similarity
  return intersection.size / union.size;
};

/**
 * Find best matching requirement in current list for a given AI requirement
 * 
 * @param aiReq - Requirement from AI response
 * @param currentReqs - Current requirements list
 * @param usedIndices - Set of indices already matched
 * @returns Index of best match or -1 if no good match found
 */
const findBestMatch = (
  aiReq: Requirements,
  currentReqs: Requirements[],
  usedIndices: Set<number>
): number => {
  let bestMatchIndex = -1;
  let bestSimilarity = 0.5; // Threshold for considering a match

  for (let i = 0; i < currentReqs.length; i++) {
    if (usedIndices.has(i)) {
      continue;
    }

    const similarity = calculateSimilarity(
      aiReq.description,
      currentReqs[i].description
    );

    if (similarity > bestSimilarity) {
      bestSimilarity = similarity;
      bestMatchIndex = i;
    }
  }

  return bestMatchIndex;
};

/**
 * Smart merge AI-generated requirements with current requirements
 * 
 * EVOLUTION STRATEGY (not replacement):
 * 1. Match AI requirements with current requirements using semantic similarity
 * 2. For matched requirements: EVOLVE by merging best aspects of both (preserve structure, enhance content)
 * 3. For unmatched current requirements: PRESERVE them (they may still be valid)
 * 4. For unmatched AI requirements: ADD them as new requirements
 * 5. Only remove requirements if they're explicitly redundant or contradictory
 * 
 * @param aiRequirements - Requirements from AI response (evolved version)
 * @param currentRequirements - Current requirements in form (to evolve)
 * @returns MergeResult with evolved requirements and change details
 */
export const smartMergeRequirements = (
  aiRequirements: Requirements[],
  currentRequirements: Requirements[]
): MergeResult => {
  // If no current requirements, return AI requirements as-is
  if (!currentRequirements || currentRequirements.length === 0) {
    return {
      mergedRequirements: aiRequirements.map((req, index) => ({
        ...req,
        id: req.id || index + 1,
        mustMatch: req.mustMatch ?? true,
      })),
      changes: {
        added: aiRequirements,
        modified: [],
        removed: [],
        unchanged: [],
      },
    };
  }

  // If no AI requirements, return current requirements
  if (!aiRequirements || aiRequirements.length === 0) {
    return {
      mergedRequirements: currentRequirements,
      changes: {
        added: [],
        modified: [],
        removed: currentRequirements,
        unchanged: [],
      },
    };
  }

  const mergedRequirements: Requirements[] = [];
  const changes = {
    added: [] as Requirements[],
    modified: [] as Requirements[],
    removed: [] as Requirements[],
    unchanged: [] as Requirements[],
  };

  const usedCurrentIndices = new Set<number>();
  const matchedPairs: Array<{ aiIndex: number; currentIndex: number }> = [];

  // Step 1: Match AI requirements with current requirements
  aiRequirements.forEach((aiReq, aiIndex) => {
    const currentIndex = findBestMatch(aiReq, currentRequirements, usedCurrentIndices);
    
    if (currentIndex >= 0) {
      matchedPairs.push({ aiIndex, currentIndex });
      usedCurrentIndices.add(currentIndex);
    }
  });

  // Step 2: Process matched pairs (EVOLVE by merging best of both)
  matchedPairs.forEach(({ aiIndex, currentIndex }) => {
    const aiReq = aiRequirements[aiIndex];
    const currentReq = currentRequirements[currentIndex];
    
    const similarity = calculateSimilarity(aiReq.description, currentReq.description);
    
    // If very similar (>0.9), consider evolved but mostly unchanged
    if (similarity > 0.9) {
      // Use AI version (may have subtle improvements) but preserve ID and structure
      mergedRequirements.push({
        ...aiReq,
        id: currentReq.id, // Preserve original ID
        mustMatch: aiReq.mustMatch ?? currentReq.mustMatch ?? true,
      });
      changes.unchanged.push(aiReq);
    } else {
      // EVOLVED: AI version is an evolution of current
      // Use AI version (evolved) but preserve ID to maintain continuity
      mergedRequirements.push({
        ...aiReq,
        id: currentReq.id, // Preserve original ID for continuity
        mustMatch: aiReq.mustMatch ?? currentReq.mustMatch ?? true,
      });
      changes.modified.push(aiReq);
    }
  });

  // Step 3: Add new requirements (not matched)
  aiRequirements.forEach((aiReq, aiIndex) => {
    const isMatched = matchedPairs.some((pair) => pair.aiIndex === aiIndex);
    if (!isMatched) {
      const newReq: Requirements = {
        ...aiReq,
        id: aiReq.id || mergedRequirements.length + 1,
        mustMatch: aiReq.mustMatch ?? true,
      };
      mergedRequirements.push(newReq);
      changes.added.push(newReq);
    }
  });

  // Step 4: Handle unmatched current requirements (PRESERVE them - evolution, not replacement)
  // Only mark as removed if they're truly redundant or explicitly contradictory
  currentRequirements.forEach((currentReq, currentIndex) => {
    if (!usedCurrentIndices.has(currentIndex)) {
      // Check if this requirement is truly redundant or contradictory
      const isRedundant = aiRequirements.some((aiReq) => {
        const similarity = calculateSimilarity(aiReq.description, currentReq.description);
        // If AI has a very similar requirement (>0.7 similarity), consider current one redundant
        return similarity > 0.7;
      });
      
      if (isRedundant) {
        // Mark as removed only if truly redundant
        changes.removed.push(currentReq);
      } else {
        // PRESERVE unmatched requirements - they may still be valid
        // Add them to merged requirements
        mergedRequirements.push({
          ...currentReq,
          id: mergedRequirements.length + 1,
        });
        // Don't mark as removed - they're preserved
      }
    }
  });

  // Ensure IDs are sequential
  mergedRequirements.forEach((req, index) => {
    req.id = index + 1;
  });

  return {
    mergedRequirements,
    changes,
  };
};

